import React, { useState } from 'react';
import './AssignedBugDetailView.scss';

const AssignedBugDetailView = ({ bug, onClose, onStatusUpdate, onNotesUpdate, currentUserEmail }) => {
  const [notes, setNotes] = useState(bug?.notes || '');
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  if (!bug) return null;
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Handle Firebase timestamp (object with seconds/nanoseconds)
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle Firebase timestamp already converted to Date
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle timestamp in milliseconds or seconds
      else if (typeof timestamp === 'number') {
        date = timestamp < 1e12 ? new Date(timestamp * 1000) : new Date(timestamp);
      }
      // Handle string timestamps
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        return 'N/A';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'N/A';
    }
  };
  
  const getSensitivityBadge = (sensitivity) => {
    const sensitivityConfig = {
      low: { color: 'success', text: 'Low' },
      medium: { color: 'warning', text: 'Medium' },
      high: { color: 'danger', text: 'High' },
      critical: { color: 'critical', text: 'Critical' }
    };
    
    const config = sensitivityConfig[sensitivity] || { color: 'secondary', text: sensitivity };
    return <span className={`sensitivity-badge ${config.color}`}>{config.text}</span>;
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'warning', text: 'Pending' },
      assigned: { color: 'info', text: 'Assigned' },
      in_progress: { color: 'primary', text: 'In Progress' },
      resolved: { color: 'success', text: 'Resolved' }
    };
    
    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };
  
  const handleStatusUpdate = async (newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await onStatusUpdate(newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };
  
  const handleNotesUpdate = async () => {
    if (notes.trim() === (bug.notes || '').trim()) return; // No change
    
    setIsUpdatingNotes(true);
    try {
      await onNotesUpdate(notes.trim());
    } catch (error) {
      console.error('Error updating notes:', error);
    } finally {
      setIsUpdatingNotes(false);
    }
  };
  
  const isAssignedToMe = bug.assignedTo?.email === currentUserEmail;
  
  return (
    <div className="assigned-bug-detail-modal">
      <div className="bug-detail-header">
        <div className="header-content">
          <h3>{bug.title}</h3>
          <div className="bug-meta">
            <div className="meta-item">
              <span className="label">Status:</span>
              <span className="value">{getStatusBadge(bug.status)}</span>
            </div>
            <div className="meta-item">
              <span className="label">Sensitivity:</span>
              <span className="value">{getSensitivityBadge(bug.sensitivity)}</span>
            </div>
            <div className="meta-item">
              <span className="label">Game/App:</span>
              <span className="value">{bug.gameName}</span>
            </div>
          </div>
        </div>
        <button className="close-button" onClick={onClose}>&times;</button>
      </div>
      
      <div className="bug-detail-content">
        <div className="bug-description">
          <h4>Description</h4>
          <p>{bug.description || 'No description provided'}</p>
        </div>
        
        {bug.stepsToReproduce && (
          <div className="bug-steps">
            <h4>Steps to Reproduce</h4>
            {Array.isArray(bug.stepsToReproduce) ? (
              <ol>
                {bug.stepsToReproduce.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            ) : (
              <p>{bug.stepsToReproduce}</p>
            )}
          </div>
        )}
        
        {bug.expectedBehavior && (
          <div className="bug-expected">
            <h4>Expected Behavior</h4>
            <p>{bug.expectedBehavior}</p>
          </div>
        )}
        
        {bug.actualBehavior && (
          <div className="bug-actual">
            <h4>Actual Behavior</h4>
            <p>{bug.actualBehavior}</p>
          </div>
        )}
        
        {bug.deviceInfo && (
          <div className="bug-device">
            <h4>Device Information</h4>
            <p>{bug.deviceInfo}</p>
          </div>
        )}
        
        <div className="bug-info-grid">
          <div className="info-section">
            <h4>Submitted By</h4>
            <div className="user-info">
              <span className="user-name">{bug.submittedBy?.name || 'N/A'}</span>
              <span className="user-email">{bug.submittedBy?.email || 'N/A'}</span>
              <span className="date">on {formatDate(bug.submittedAt)}</span>
            </div>
          </div>
          
          <div className="info-section">
            <h4>Assigned To</h4>
            <div className="user-info">
              {bug.assignedTo ? (
                <>
                  <span className="user-name">{bug.assignedTo.name}</span>
                  <span className="user-email">{bug.assignedTo.email}</span>
                  {isAssignedToMe && <span className="assigned-to-me">You</span>}
                </>
              ) : (
                <span className="not-assigned">Not Assigned</span>
              )}
            </div>
          </div>
          
          {bug.lastUpdated && (
            <div className="info-section">
              <h4>Last Updated</h4>
              <div className="update-info">
                <span className="date">{formatDate(bug.lastUpdated)}</span>
                {bug.updatedBy && (
                  <span className="updated-by">by {bug.updatedBy.name}</span>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Notes Section */}
        <div className="notes-section">
          <h4>My Notes</h4>
          <div className="notes-form">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add your notes about this bug..."
              rows={4}
              disabled={isUpdatingNotes}
            />
            <div className="notes-actions">
              <button 
                className="save-notes-btn"
                onClick={handleNotesUpdate}
                disabled={isUpdatingNotes || notes.trim() === (bug.notes || '').trim()}
              >
                {isUpdatingNotes ? 'Saving...' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
        
        {bug.images && bug.images.length > 0 && (
          <div className="bug-screenshots">
            <h4>Screenshots</h4>
            <div className="screenshots-grid">
              {bug.images.map((image, index) => (
                <div key={index} className="screenshot-item">
                  <a href={image} target="_blank" rel="noopener noreferrer">
                    <img src={image} alt={`Screenshot ${index + 1}`} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="bug-detail-footer">
        {isAssignedToMe && (
          <div className="status-actions">
            <span className="status-label">Update Status:</span>
            {bug.status !== 'in_progress' && (
              <button 
                className="status-button in-progress"
                onClick={() => handleStatusUpdate('in_progress')}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Mark In Progress'}
              </button>
            )}
            {bug.status !== 'resolved' && (
              <button 
                className="status-button resolved"
                onClick={() => handleStatusUpdate('resolved')}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? 'Updating...' : 'Mark Resolved'}
              </button>
            )}
          </div>
        )}
        <button className="close-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default AssignedBugDetailView;